import React, { useState, useEffect } from 'react';
import { getProperty, getReferencePhotos, uploadReferencePhoto, deleteReferencePhoto, getReferencePhotoUrl, updateProperty } from '../services/api';
import { generateSpacesConfig, displayName } from '../utils/propertyConstants';

const STANDARD_PREFIXES = ['Sala de estar', 'Dormitorio', 'Baño', 'Cocina'];

const isStandardSpace = (space) => {
  return STANDARD_PREFIXES.some(prefix => (space.name || '').startsWith(prefix));
};

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const PropertyReferencePhotosTab = ({ propertyId }) => {
  const [property, setProperty] = useState(null);
  const [refPhotos, setRefPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingRef, setUploadingRef] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => { fetchAll(); }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const prop = await getProperty(propertyId);
      if (prop) {
        if (!prop.spaces_config || prop.spaces_config.length === 0) {
          prop.spaces_config = generateSpacesConfig(
            prop.num_bedrooms || 1,
            prop.num_bathrooms || 1,
            prop.has_kitchen !== false,
            prop.has_living_room !== false
          );
        }
        setProperty(prop);
      }
      const photos = await getReferencePhotos(propertyId);
      setRefPhotos(photos || []);
    } catch (err) {
      console.error('Error fetching reference photos data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (spaceName, file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert('Only JPG, PNG, and WebP files are allowed.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File size must be under 5 MB.');
      return;
    }
    setUploadingRef(spaceName);
    try {
      await uploadReferencePhoto(propertyId, spaceName, file);
      const photos = await getReferencePhotos(propertyId);
      setRefPhotos(photos || []);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingRef(null);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this reference photo?')) return;
    try {
      await deleteReferencePhoto(photoId);
      setRefPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed. Please try again.');
    }
  };

  const handleToggleRequired = async (spaceIndex, isRequired) => {
    if (!property) return;
    const updated = [...property.spaces_config];
    updated[spaceIndex] = { ...updated[spaceIndex], required: isRequired };
    try {
      await updateProperty(propertyId, { spaces_config: updated });
      setProperty(prev => ({ ...prev, spaces_config: updated }));
    } catch (err) {
      console.error('Failed to update space:', err);
    }
  };

  const handleRemoveSpace = async (spaceIndex) => {
    if (!property) return;
    const space = property.spaces_config[spaceIndex];
    const label = displayName(space);
    if (!window.confirm(`Remove "${label}" from spaces?`)) return;
    const updated = property.spaces_config.filter((_, i) => i !== spaceIndex);
    // Re-number order
    updated.forEach((s, i) => { s.order = i + 1; });
    try {
      await updateProperty(propertyId, { spaces_config: updated });
      setProperty(prev => ({ ...prev, spaces_config: updated }));
    } catch (err) {
      console.error('Failed to remove space:', err);
    }
  };

  const handleAddSpace = async () => {
    if (!property) return;
    const nameEs = prompt('Space name (Spanish):');
    if (!nameEs || !nameEs.trim()) return;
    const nameEn = prompt('Space name (English):');
    if (!nameEn || !nameEn.trim()) return;
    const updated = [
      ...property.spaces_config,
      {
        name: nameEs.trim(),
        name_en: nameEn.trim(),
        order: property.spaces_config.length + 1,
        required: false,
      },
    ];
    try {
      await updateProperty(propertyId, { spaces_config: updated });
      setProperty(prev => ({ ...prev, spaces_config: updated }));
    } catch (err) {
      console.error('Failed to add space:', err);
    }
  };

  const triggerFileInput = (spaceName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handleUpload(spaceName, file);
    };
    input.click();
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    );
  }

  // ── Empty state ──
  if (!property || !property.spaces_config || property.spaces_config.length === 0) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
        No spaces configured. Go to Settings tab to set up room structure.
      </div>
    );
  }

  const spaces = property.spaces_config;

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      {/* Section header */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Reference Photos
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
          Upload gold-standard photos for each space. These are sent to cleaners before checkout.
        </p>
      </div>

      {/* Spaces list */}
      {spaces.map((space, idx) => {
        const label = displayName(space);
        const isRequired = space.required !== false;
        const spacePhotos = refPhotos.filter(p => p.space_name === space.name);
        const isUploading = uploadingRef === space.name;
        const canRemove = !isStandardSpace(space);

        return (
          <div
            key={`${space.name}-${idx}`}
            style={{
              padding: '12px',
              backgroundColor: 'var(--bg-elevated)',
              borderRadius: '8px',
              marginBottom: '12px',
              border: '1px solid var(--border-medium)',
            }}
          >
            {/* Space header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', flex: 1 }}>
                {label}
              </span>

              {/* Required / Optional badge */}
              <span style={{
                padding: '2px 8px',
                fontSize: '11px',
                borderRadius: '10px',
                backgroundColor: isRequired ? 'var(--badge-green-bg)' : 'var(--badge-yellow-bg)',
                color: isRequired ? 'var(--badge-green-text)' : 'var(--badge-yellow-text)',
              }}>
                {isRequired ? 'Required' : 'Optional'}
              </span>

              {/* Toggle dropdown */}
              <select
                value={isRequired ? 'required' : 'optional'}
                onChange={(e) => handleToggleRequired(idx, e.target.value === 'required')}
                style={{
                  fontSize: '11px',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-input)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                <option value="required">Required</option>
                <option value="optional">Optional</option>
              </select>

              {/* Remove button for custom spaces */}
              {canRemove && (
                <button
                  onClick={() => handleRemoveSpace(idx)}
                  title="Remove space"
                  style={{
                    fontSize: '12px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-input)',
                    backgroundColor: 'var(--bg-input)',
                    color: 'var(--badge-red-text)',
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              )}
            </div>

            {/* Photo grid */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              {spacePhotos.map((photo) => (
                <div key={photo.id} style={{ position: 'relative' }}>
                  <img
                    src={getReferencePhotoUrl(photo.file_path)}
                    alt={`${label} reference`}
                    onClick={() => setLightboxSrc(getReferencePhotoUrl(photo.file_path))}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: '2px solid var(--color-primary)',
                      cursor: 'pointer',
                    }}
                  />
                  <button
                    onClick={() => handleDelete(photo.id)}
                    title="Delete photo"
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--badge-red-text)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    x
                  </button>
                </div>
              ))}

              {/* Upload (+) button */}
              <div
                onClick={() => !isUploading && triggerFileInput(space.name)}
                style={{
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed var(--border-strong)',
                  borderRadius: '6px',
                  cursor: isUploading ? 'wait' : 'pointer',
                  backgroundColor: 'var(--bg-card)',
                  fontSize: '24px',
                  color: 'var(--text-faint)',
                  opacity: isUploading ? 0.5 : 1,
                }}
                title="Upload reference photo"
              >
                {isUploading ? '...' : '+'}
              </div>
            </div>

            {/* Checklist items from AI analysis */}
            {spacePhotos.some(p => p.checklist_items && p.checklist_items.length > 0) && (
              <div style={{ marginTop: '8px' }}>
                {spacePhotos
                  .filter(p => p.checklist_items && p.checklist_items.length > 0)
                  .flatMap(p => p.checklist_items)
                  .filter((item, i, arr) => arr.indexOf(item) === i) // deduplicate
                  .map((item, i) => (
                    <div key={i} style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginLeft: '8px', lineHeight: '1.6' }}>
                      {'\u2610'} {item}
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Add custom space button */}
      <button
        onClick={handleAddSpace}
        style={{
          width: '100%',
          padding: '10px',
          fontSize: '13px',
          fontWeight: '500',
          borderRadius: '8px',
          border: '2px dashed var(--border-strong)',
          backgroundColor: 'var(--bg-card)',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          marginTop: '4px',
        }}
      >
        + Add Custom Space
      </button>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer',
          }}
        >
          <img
            src={lightboxSrc}
            alt="Full size"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PropertyReferencePhotosTab;
